import React, { useEffect, useMemo, useState } from 'react';
import _, { partition, uniq, sortBy } from 'lodash';
import moment from 'moment';
import { useSwipeable } from 'react-swipeable';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { Item, Order, ORDER_ORIGIN } from '@baemingo/order-status-events/dist/types';

import { ORDER_STATUS } from '../../../../../__queries__';
import { SERVICE_TYPE } from '~../__queries__/globalTypes';
import { UpdateOrder } from '../../Queries/__queries__/UpdateOrder';
import { GetOrder_order } from '../../Queries/__queries__/GetOrder';
import { GetTable, GetTableVariables } from '../../Queries/__queries__';

import OrderItem from '../OrderItem';
import takeAwayIcon from '~Assets/icons/local_mall.svg';
import { useOrderStatusApi } from '~Components/OrderStatusUniverse';
import { useStorage } from '~Components/Storage';
import { formatOrderType } from '~utilities';
import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';

import OrderETA from './OrderETA';
import { LocalItem } from '~Types/index';
import { ITEM_NAME_QUERY } from '../ItemName';
import { VARIATION_NAME_QUERY } from '../VariationName';
import { UPDATE_ORDER } from '../../Queries/orders';
import { GET_TABLE } from '~Queries/tables';
import { ItemWithCourseType } from '~Scenes/EventedKitchenDisplay';
import { getCompanyData_Company_courseTypes } from '~Scenes/EventedKitchenDisplay/__queries__';
import Time from './Time';
import { extractCleanComment, getPickupTime, getThirdPartyMetadata } from './utils';
import { DateTime } from 'luxon';
import { Box } from '~Styles';
import Origin from './Origin';
import { useIncomingAppOrders } from '~Providers/IncomingAppOrdersProvider';
import { itemInBong } from '~Scenes/POS/Queries/__queries__';
import { ITEM_FRAGMENT } from '~Scenes/POS/Queries/query';

export interface MultiplierStyleProps {
  styleMultiplier: {
    size?: number;
    space?: number;
  };
}

const Wrapper = styled.div<{
  pressed: boolean;
  isBuffering?: boolean;
}>`
  background: var(--colorBlueGray100);
  border-radius: 4px;
  overflow: hidden;
  color: var(--colorWhite);
  position: relative;
  transition: all 100ms;
  transform: perspective(500px) translateZ(${(props) => (props.pressed ? -15 : 0)}px);
  opacity: ${(props) => (props.isBuffering || props.pressed ? 0.5 : 1)};
  transform-origin: 50% 50%;
  display: flex;
  flex-flow: column nowrap;
`;

const Header = styled.div`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
`;

const HeaderText = styled.div<{
  styleMultiplier: {
    space: number;
  };
}>`
  flex: 1;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: 0px calc(16px - 0.2em * ${({ styleMultiplier }) => styleMultiplier?.space});
`;

const TypeIcon = styled.div`
  width: 24px;
  height: 24px;
  background-color: #fff;
  margin-left: 8px;
`;

const TakeAwayIcon = styled(TypeIcon)`
  mask-image: url(${takeAwayIcon});
`;

const OrderType = styled.span<{
  styleMultiplier: {
    size: number;
  };
}>`
  letter-spacing: 0.24px;
  color: var(--colorWhite);
  text-align: center;
  font-size: calc(20px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
`;

const OrderNumberWrapper = styled.div<{
  orderType: SERVICE_TYPE;
  styleMultiplier: {
    space: number;
  };
}>`
  background-color: ${(props) =>
    props.orderType == 'TAKE_AWAY' ? 'var(--colorUIYellow)' : 'var(--colorWhite)'};
  padding: ${({ styleMultiplier }) =>
    `calc(4px - 0.2em * ${styleMultiplier?.space}) calc(16px - 0.2em * ${styleMultiplier?.space})`};
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  min-height: 32px;
`;

const OrderId = styled.span<MultiplierStyleProps>`
  text-align: center;
  color: #1c2733;
  letter-spacing: 0.24px;
  text-transform: uppercase;
  margin-right: auto;
  font-size: calc(20px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
  line-height: calc(40px - 0.2em * ${({ styleMultiplier }) => styleMultiplier?.size});
`;

const Comment = styled(Box)`
  color: #333;
  background: var(--colorWhite);
  padding: 10px 5px 10px 10px;
  font-size: 14px;
  font-style: italic;
`;

const DesiredTime = styled.div<MultiplierStyleProps>`
  display: flex;
  flex-flow: row wrap;
  background-color: var(--colorBlueGray100);
  border-bottom: 1px solid var(--colorBlueGray200);
  align-items: center;
  color: #1c232c;
  font-size: calc(24px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
  font-weight: 600;
  line-height: calc(32px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
  padding: ${({ styleMultiplier }) =>
    `calc(12px - 0.2em * ${styleMultiplier?.space}) calc(16px - 0.2em * ${styleMultiplier?.space})`};
`;

const OrderGroupItems = styled.div``;

const CourseTypeName = styled.div<{
  color?: string;
}>`
  padding: 10px;
  background: ${({ color }) => color || '#dedede'};
  color: #000;
  font-size: 14px;
`;

const HeaderCourseBadge = styled.div<{
  color?: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ color }) => color || '#dedede'};
  color: #000;
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  font-weight: 500;
`;

export const OrderItemWrapper = styled.div<{
  padding: boolean;
  isGroup: boolean;
  itemStatusColor: string;
  faded?: boolean;
}>`
  padding-bottom: ${({ padding }) => (padding ? '16px' : '0')};
  margin-left: ${({ isGroup }) => (isGroup ? '10px' : '0')};
  opacity: ${({ faded }) => (faded ? '0.5' : '1')};
  transform: ${({ faded }) => (faded ? 'scale(0.95)' : 'scale(1)')};
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
`;

const LiteWrapper = styled.div<{
  pressed: boolean;
  isBuffering?: boolean;
}>`
  border-radius: 4px;
  transition: all 100ms;
  transform: perspective(500px) translateZ(${(props) => (props.pressed ? -15 : 0)}px);
  opacity: ${(props) => (props.isBuffering || props.pressed ? 0.5 : 1)};
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  flex-shrink: 0;
`;

const LiteOrderHeader = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 0 16px;
  color: #fff;
`;

const LiteOrderId = styled.div<MultiplierStyleProps>`
  display: flex;
  align-items: center;
  text-align: center;
  font-size: calc(20px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
`;

const LiteTypeHeading = styled.div<MultiplierStyleProps>`
  font-size: calc(20px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
  color: var(--colorWhite);
  margin-left: auto;
  margin-right: 16px;
`;

const nextStatus = (status: ORDER_STATUS) => {
  switch (status) {
    case ORDER_STATUS.PENDING:
      return ORDER_STATUS.ACCEPTED;
    case ORDER_STATUS.ACCEPTED:
      return ORDER_STATUS.FINALIZING;
    case ORDER_STATUS.FINALIZING:
      return ORDER_STATUS.READY;
    case ORDER_STATUS.READY:
      return ORDER_STATUS.COMPLETED;
    default:
      return ORDER_STATUS.ACCEPTED;
  }
};

export const statusBackgroundColor = (status: ORDER_STATUS) => {
  switch (status) {
    case ORDER_STATUS.ACCEPTED:
      return '#1642be';
    case ORDER_STATUS.FINALIZING:
      return '#ff9100';
    case ORDER_STATUS.READY:
      return '#209946';
    default:
      return '#515A5A';
  }
};

export const getOriginBackgroundColor = (type?: string | null) => {
  switch (type) {
    case 'FOODORA':
      return '#df1067';
    case 'WOLT':
      return '#00c2e8';
    case 'UBER_EATS':
      return '#03c167';
    case 'DOOR_DASH':
      return '#eb1700';
    case 'EXPRESS': //Baemingo
      return '#646195';
    case 'APP': // Baemingo
      return '#4f46e5';
    default:
      // POS
      return '#2f353d';
  }
};

type CombinedItem = { count: number } & Item;

// Compare variations
const compareVariations = (a: Item, b: Item) =>
  a.variations.join('-') === b.variations.join('-') &&
  a.removedVariations.join('-') === b.removedVariations.join('-');

// Check if the variations and message is an exact match
const exactMatch = (a: Item, b: Item, groupByCourseTypes?: boolean) => {
  if (groupByCourseTypes) {
    return (
      a.itemId === b.itemId &&
      a.comment === b.comment &&
      a.status === b.status &&
      a.courseId === b.courseId &&
      compareVariations(a, b)
    );
  } else {
    return (
      a.itemId === b.itemId &&
      a.comment === b.comment &&
      a.status === b.status &&
      compareVariations(a, b)
    );
  }
};

export const combineKdsOrderItems = (
  items: Item[],
  groupByCourseTypes?: boolean,
): CombinedItem[] =>
  items
    .filter(
      (item, i) => items.findIndex((b: Item) => exactMatch(item, b, groupByCourseTypes)) === i,
    )
    .map((item) => ({
      ...item,
      count: items.filter((b) => exactMatch(item, b, groupByCourseTypes)).length,
    }));

interface Props {
  order: Order;
  realOrder?: GetOrder_order | null;
  items: Item[];
  courseTypes?: getCompanyData_Company_courseTypes[];
  itemsWithCourseType?: ItemWithCourseType[];
  venueId: string;
  disableUserInteraction?: boolean; // Disables user interactions, ETA etc, set to true when Order component is displayed outside KDS
}

const OrderComponent: React.FunctionComponent<Props> = ({
  order,
  realOrder,
  items,
  courseTypes,
  itemsWithCourseType,
  venueId,
  disableUserInteraction,
}) => {
  const client = useApolloClient();
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const api = useOrderStatusApi(venueId);
  const [storage] = useStorage();
  const logger = useLogger('kds-order');
  const printers = useSelector((state) => state.printers);
  const station = useSelector((state) => {
    if (!state.stations || !storage.selectedKDSStation) {
      return undefined;
    }

    return state.stations.find((s) => s.id === storage.selectedKDSStation);
  });
  const incomingAppOrders = useIncomingAppOrders();
  const [touching, setTouching] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState(moment());

  const [pickupTimeRaw, setPickupTimeRaw] = useState<string | null>(null);

  const tableQuery = useQuery<GetTable, GetTableVariables>(GET_TABLE, {
    variables: {
      tableId: order.tableId || '',
    },
    skip: !order.tableId,
  });

  const groupByCourseTypes = !disableUserInteraction && storage.KDSGroupItemsByCourseType;

  const [updateOrder] = useMutation<UpdateOrder>(UPDATE_ORDER);

  const handlers = useSwipeable({
    delta: 10,
    onSwiping: () => setTouching(false),
  });

  // Hanlde touching the Head part of the order
  const handleHeadTouchEnd = async () => {
    // Abort order progress based on KDS settings, or if userMutations are disabled at component render
    if (storage.disableOrderProgress || disableUserInteraction) {
      logger.warn('Order progress blocked (KDS settings or early component render)');
      setTouching(false);
      return;
    }

    // Find item that is not in stations orderStatuses
    const itemNotInOrderStatus = items.find((i) => !station?.orderStatuses.includes(i.status));

    // If there are items that are not in the stations selected orderStatuses, dont progress the order
    if (storage.KDSShowAllOrderStatuses && !!itemNotInOrderStatus) {
      logger.warn('Order progress blocked (item not in selected station`s orderStatuses)');
      setTouching(false);
      return;
    }

    if (touching) {
      const tripleProgress =
        storage.tripleOrderItemProgress &&
        (order.serviceType !== SERVICE_TYPE.TAKE_AWAY || storage.KDSBongOnProgress);

      for (let i = 0; i < (tripleProgress ? 3 : storage.KDSLite ? 2 : 1); i++) {
        if (storage.KDSProgressOnlyFiltered) {
          for (const item of items) {
            api.progressItem(item.id);
          }
        } else {
          api.progressOrder(order.id);
        }
      }

      const status = nextStatus(order.status);

      const shouldBongBecauseOfReady = storage.KDSBongOnReady && status === ORDER_STATUS.READY;
      const shouldBongBecauseOfProgress =
        storage.KDSBongOnProgress && status !== ORDER_STATUS.COMPLETED;

      if (shouldBongBecauseOfReady || shouldBongBecauseOfProgress) {
        // Map the item to a format that fits the print function
        // The await might look scary (time-consuming) but we have two instances of cache reading before we hit the actual query. And next time the item comes in it should be available in cache
        const itemsForPrint: LocalItem[] = await Promise.all(
          items.map(async (item) => {
            let itemFragment = client.readFragment<itemInBong>({
              id: `Item${item.itemId}`,
              fragment: ITEM_FRAGMENT,
            });

            if (!i18n(itemFragment?.titleLang)) {
              itemFragment = client.readQuery({
                query: ITEM_NAME_QUERY,
                variables: {
                  id: item.itemId,
                },
              })?.item;
            }

            if (!i18n(itemFragment?.titleLang)) {
              logger.warn(`Item fragment not found for itemId: ${item.itemId}, fetching...`);

              // Fetch the item name before continuing so we have it for the print
              const result = await client.query({
                query: ITEM_NAME_QUERY,
                variables: { id: item.itemId },
                fetchPolicy: 'cache-first',
              });

              itemFragment = result.data?.item || null;
            }

            const title = itemFragment?.titleLang;

            return {
              ...item,
              description: '',
              message: item.comment,
              vatRate: 0, // Not used
              variations: item.variations.map((id) => {
                const variationFragment = client.readQuery({
                  query: VARIATION_NAME_QUERY,
                  variables: {
                    id,
                  },
                })?.variation;

                const variationTitle = variationFragment?.nameLang;

                return {
                  id,
                  name: variationTitle ? i18n(variationTitle) : id,
                  price: 0,
                };
              }),
              parentId: undefined,
              removedVariations: item.removedVariations.map((id) => {
                const variationFragment = client.readQuery({
                  query: VARIATION_NAME_QUERY,
                  variables: {
                    id,
                  },
                })?.variation;

                const variationTitle = variationFragment?.nameLang;

                return {
                  id,
                  name: variationTitle ? i18n(variationTitle) : id,
                  price: 0,
                };
              }),
              title: title ? i18n(title) : item.itemId,
            };
          }),
        );

        // Log if no printer has a station matching the selected station
        const printerWithKDSStation =
          !!storage.selectedKDSStation &&
          !!printers.find((p) => p.stations.includes(storage.selectedKDSStation!));

        if (!printerWithKDSStation) {
          logger.warn(
            'Trying to bong KDS ticket but no printer has a station matching the selected KDS station',
            {
              printers,
              selectedKDSStation: storage.selectedKDSStation,
            },
          );
        }

        dispatch({
          type: 'PRINT_PHYSICAL_BONG',
          items: itemsForPrint,
          order: {
            id: order.id,
            comment: order.comment,
            orderNumber: order.tableName || order.easyId,
            chequeName: order.tableId ? order.tableName : null,
            desiredTime: realOrder?.desiredTime || order.estimatedFinishTime,
          },
          serviceType: order.serviceType,
        });
      }

      /**
       * If this order should update an actual "Order" in the DB (legacy system),
       * run a mutation to update it. This is primarily so we can manage app orders.
       */
      if (!realOrder) {
        return;
      }

      updateOrder({
        variables: {
          orderId: order.realId,
          status,
        },
      })
        .then((res) => {
          // Some kind of verification that the update went though
          if (res.data?.updateOrder?.status !== status) {
            api.undo();
          } else {
            /**
             * If we have triple progress enabled (which, in other words means "move to COMPLETED on click")
             * and the status we updated the order to was not COMPLETED then we will have a mismatch between
             * the KDS order and the "core order". This is because the KDS order will be in COMPLETED state
             * while the core order will be in some in-progress state. To fix this, we will run a mutation
             * to update the core order to COMPLETED as well.
             */
            if (tripleProgress && status !== ORDER_STATUS.COMPLETED) {
              updateOrder({
                variables: {
                  orderId: order.realId,
                  status: ORDER_STATUS.COMPLETED,
                },
              });
            }
          }
        })
        .catch((e) => {
          api.undo();
        });
    }
  };

  const progressItem = (item: Item): void => {
    if (storage.disableItemProgress) {
      return;
    }

    const tripleProgress =
      storage.tripleOrderItemProgress && order.serviceType !== SERVICE_TYPE.TAKE_AWAY;

    for (let i = 0; i < (tripleProgress ? 3 : 1); i++) {
      api.progressItem(item.id);
    }

    // Handle bonging for individual item progress
    // Only bong if the item progress setting is enabled
    if (storage.KDSBongOnItemProgress) {
      // Read result of ITEM_NAME_QUERY from cache
      const itemFragment = client.readQuery({
        query: ITEM_NAME_QUERY,
        variables: {
          id: item.itemId,
        },
      })?.item;

      // Map the single item to a format that fits the print function
      const itemForPrint: LocalItem = {
        ...item,
        description: '',
        message: item.comment,
        vatRate: 0, // Not used
        variations: item.variations.map((id) => {
          const variationFragment = client.readQuery({
            query: VARIATION_NAME_QUERY,
            variables: {
              id,
            },
          })?.variation;

          const variationTitle = variationFragment?.nameLang;

          return {
            id,
            name: variationTitle ? i18n(variationTitle) : id,
            price: 0,
          };
        }),
        parentId: undefined,
        removedVariations: item.removedVariations.map((id) => {
          const variationFragment = client.readQuery({
            query: VARIATION_NAME_QUERY,
            variables: {
              id,
            },
          })?.variation;

          const variationTitle = variationFragment?.nameLang;

          return {
            id,
            name: variationTitle ? i18n(variationTitle) : id,
            price: 0,
          };
        }),
        title: itemFragment?.titleLang ? i18n(itemFragment.titleLang) : item.itemId,
      };

      // Log if no printer has a station matching the selected station
      const printerWithKDSStation =
        !!storage.selectedKDSStation &&
        !!printers.find((p) => p.stations.includes(storage.selectedKDSStation!));

      if (!printerWithKDSStation) {
        logger.warn(
          'Trying to bong KDS ticket for item but no printer has a station matching the selected KDS station',
          {
            printers,
            selectedKDSStation: storage.selectedKDSStation,
            item,
          },
        );
      }

      logger.info('Bonging single item on progress', {
        itemId: item.id,
        KDSBongOnItemProgress: storage.KDSBongOnItemProgress,
      });

      dispatch({
        type: 'PRINT_PHYSICAL_BONG',
        items: [itemForPrint],
        order: {
          id: order.id,
          comment: order.comment,
          orderNumber: order.tableName || order.easyId,
          chequeName: order.tableId ? order.tableName : null,
          desiredTime: realOrder?.desiredTime || order.estimatedFinishTime,
        },
        serviceType: order.serviceType,
      });
    }

    /**
     * If this order should update an actual "Order" in the DB (legacy system),
     * run a mutation to update it. This is primarily so we can manage app orders.
     */
    if (!realOrder) {
      return;
    }

    const status = nextStatus(item.status);

    updateOrder({
      variables: {
        orderId: order.realId,
        status,
      },
    })
      .then((res) => {
        // Some kind of verification that the update went though
        if (res.data?.updateOrder?.status !== status) {
          api.undo();
        }
      })
      .catch((e) => {
        api.undo();
      });
  };

  useEffect(() => {
    setTouching(false);
  }, [order]);

  const stackedItems = useMemo(() => {
    const [stackableItems, rest] = partition(items, (item) => !item.parentId);
    const combined: CombinedItem[] = [
      ...combineKdsOrderItems(stackableItems, groupByCourseTypes),
      ...rest.map((i) => ({ ...i, count: 1 })),
    ];

    // Sorting by parentId or id
    // parentId is set to either an upsells "parent" id or if the item is a parent, its own id
    // This keeps the order cohesive and makes it possible to group items by parentId
    const hasGroups = combined.some((i) => i.parentId);
    return combined
      .sort((a, b) => (a.itemId > b.itemId ? 1 : -1))
      .sort((a, b) => (a.status > b.status ? 1 : -1))
      .sort((a, b) => (hasGroups ? (a.id === b.parentId ? 1 : -1) : 0))
      .sort((a, b) => (hasGroups ? ((a.parentId || a.id) > (b.parentId || b.id) ? 1 : -1) : 0));
  }, [items, groupByCourseTypes]);

  const orderCourseType = useMemo(() => {
    if (!order.courseId || !courseTypes || courseTypes.length === 0) {
      return null;
    }

    return courseTypes.find((c) => c.id === order.courseId) || null;
  }, [order.courseId, courseTypes]);

  const groupedByCourseTypesItems = useMemo(() => {
    if (
      !courseTypes ||
      courseTypes.length === 0 ||
      !itemsWithCourseType ||
      itemsWithCourseType.length === 0 ||
      !groupByCourseTypes
    ) {
      return [];
    }

    const itemsWithCourseTypeId: CombinedItem[] = [];

    // Create items with courseTypeId
    stackedItems.forEach((stackedItem) => {
      let courseId = order.courseId ?? null;
      if (!courseId && stackedItem.courseId) {
        courseId = stackedItem.courseId;
      } else if (!courseId) {
        const item = itemsWithCourseType.find((i) => i.itemId === stackedItem.itemId);
        courseId = item?.courseId ?? null;
      }

      itemsWithCourseTypeId.push({
        ...stackedItem,
        courseId,
      });
    });

    // Array of unique course type ids
    const ids = itemsWithCourseTypeId.filter((i) => i.courseId).map((i) => i.courseId);
    const uniqIds = uniq(ids);

    // Create groups items with courseType
    let resultItems = [];

    uniqIds.forEach((id) => {
      const filtered = itemsWithCourseTypeId.filter((i) => i.courseId === id);

      if (filtered.length > 0) {
        const courseType = courseTypes.find((c) => c.id === id);

        if (courseType) {
          resultItems.push({
            courseId: id,
            name: courseType.name,
            label: courseType.label,
            order: courseType.order,
            color: courseType.color,
            items: filtered,
          });
        }
      }
    });

    // Items that do not have a course type
    const itemsWithoutCourseType = itemsWithCourseTypeId.filter((i) => !i.courseId);

    if (itemsWithoutCourseType.length > 0) {
      resultItems.push({
        courseId: 'course-type-missing',
        name: 'KDS.NoCourseType',
        label: '',
        order: null,
        color: undefined,
        items: itemsWithoutCourseType,
      });
    }

    resultItems = sortBy(resultItems, [({ order }) => order || Infinity]);

    return resultItems;
  }, [stackedItems, groupByCourseTypes, itemsWithCourseType, order.courseId]);

  /* Third party orders */
  const thirdPartyMetadata = order.comment ? getThirdPartyMetadata(order.comment) : null;

  const pickupTime = getPickupTime(
    thirdPartyMetadata?.desiredTime || realOrder?.desiredTime || order.estimatedFinishTime,
  );

  const isFutureOrder = realOrder?.estimatedFinishTime
    ? DateTime.fromISO(realOrder.estimatedFinishTime).startOf('day') >
      DateTime.now().startOf('day')
    : false;

  const renderHeaderInfoTime = () => {
    return realOrder?.estimatedFinishTime && !storage.showOrderEtaBar && !isFutureOrder ? (
      <OrderETA
        estimatedFinishTime={pickupTimeRaw || realOrder?.estimatedFinishTime}
        orderId={realOrder.id}
        disabled={disableUserInteraction}
        onChangePickupTime={setPickupTimeRaw}
      />
    ) : (
      <Time
        pickupTimeRaw={
          pickupTimeRaw ||
          thirdPartyMetadata?.desiredTime ||
          realOrder?.estimatedFinishTime ||
          realOrder?.desiredTime ||
          order.estimatedFinishTime ||
          realOrder?.estimatedFinishTime
        }
        onChangePickupTime={setPickupTimeRaw}
        styleMultiplier={storage.styleMultiplier}
        createdAt={order.createdAt}
      />
    );
  };

  const tableCode = tableQuery.data?.table?.code || null;
  const isBuffering =
    Boolean(order.tableId) &&
    Boolean(tableCode) &&
    order.status === ORDER_STATUS.ACCEPTED &&
    currentTime.isBefore(moment(order.createdAt).add(180, 'seconds'));

  const bumpCurrentTime = () => {
    setCurrentTime(moment());
  };

  useEffect(() => {
    // This useEffects purpose is to help the "isBuffering" boolean
    // to decide wether or not an order should be in a buffering state.
    // It uses an interval to bump the currentTime state.

    // Calculate the seconds-difference from currentTime and when the order was created.
    const secondsDifference = currentTime.diff(moment(order.createdAt), 'seconds');

    // If the seconds-difference is higher than 180,
    // then 3 minutes has passed since the order was created.
    // And we don't need to add any intervals.
    if (secondsDifference > 180) {
      return;
    }

    // If 3 minutes have not passed, add an interval to when
    // we should bump the currentTime state.
    // This should only apply to QR Table orders.
    if (Boolean(order.tableId) && Boolean(tableCode)) {
      // Add an interval that bumps currentTime after 5 seconds.
      const intervalId = setInterval(() => bumpCurrentTime(), 5000);
      return () => clearInterval(intervalId);
    }
    return;
  }, []);

  // Force render every second for the first 5 seconds of order lifetime
  // This is tied to the "isNewOrder" conditional render below
  useEffect(() => {
    const intervalId = setInterval(() => {
      bumpCurrentTime();
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Hide order if its newer than 3 seconds
  // This is to prevent the order from being progressed before it "exists" due to
  // android screens local time diffing up to 1 second between each other
  const orderIsNew = moment().diff(moment(order.createdAt), 'seconds') < 3;
  if (orderIsNew) {
    return null;
  }

  const getOrderTitle = (): string => {
    if (order.origin === 'THIRD_PARTY' && order.easyId) {
      return order.easyId;
    }

    if (order.tableName) {
      return order.tableName.includes('•') ? order.tableName : `#${order.tableName}`;
    }

    return order.id.slice(0, 4);
  };

  if (storage.KDSLite) {
    return (
      <LiteWrapper
        {...handlers}
        pressed={isBuffering || touching}
        style={{
          backgroundColor: getOriginBackgroundColor(
            order.serviceType === 'DELIVERY' ? realOrder?.payment?.type : order.origin,
          ),
        }}
        onTouchStart={() => {
          if (
            !storage.disableOrderProgress ||
            (order.origin === 'APP' && order.serviceType === SERVICE_TYPE.TAKE_AWAY)
          ) {
            setTouching(true);
          }
        }}
        onTouchEnd={handleHeadTouchEnd}>
        <LiteOrderHeader>
          <LiteOrderId styleMultiplier={storage.styleMultiplier}>
            {order.tableName && order.tableId ? (
              <span>{`${i18n('KDS.Table')} ${order.tableName} (#${order.id.slice(0, 4)})`}</span>
            ) : (
              <span>{getOrderTitle()}</span>
            )}
            {order.serviceType === SERVICE_TYPE.TAKE_AWAY && <TakeAwayIcon />}
          </LiteOrderId>
          <LiteTypeHeading styleMultiplier={storage.styleMultiplier}>
            {order.serviceType === 'DELIVERY'
              ? realOrder?.payment?.type.replace('_', ' ') ?? 'DELIVERY'
              : order.origin}
          </LiteTypeHeading>

          {renderHeaderInfoTime()}
        </LiteOrderHeader>
      </LiteWrapper>
    );
  }

  return (
    <Wrapper {...handlers} pressed={isBuffering || touching}>
      {!tableCode && !storage.KDSHideOrderHeader && (
        <OrderNumberWrapper
          styleMultiplier={storage.styleMultiplier}
          orderType={order.serviceType || SERVICE_TYPE.EAT_HERE}>
          {order.tableName && order.tableId ? (
            <OrderId styleMultiplier={storage.styleMultiplier}>{`${i18n('KDS.Table')} ${
              order.tableName
            } (#${order.id.slice(0, 4)})`}</OrderId>
          ) : (
            <OrderId styleMultiplier={storage.styleMultiplier}>{getOrderTitle()}</OrderId>
          )}
          {order.serviceType === SERVICE_TYPE.TAKE_AWAY && <TakeAwayIcon />}
        </OrderNumberWrapper>
      )}

      <Header
        onTouchStart={() => {
          if (
            !storage.disableOrderProgress ||
            (order.origin === 'APP' && order.serviceType === SERVICE_TYPE.TAKE_AWAY)
          ) {
            setTouching(true);
          }
        }}
        onTouchEnd={handleHeadTouchEnd}
        style={{
          backgroundColor: statusBackgroundColor(order.status as ORDER_STATUS),
        }}>
        <HeaderInfo>
          <HeaderText styleMultiplier={storage.styleMultiplier}>
            <OrderType styleMultiplier={storage.styleMultiplier}>
              {storage.KDSHideOrderHeader ? getOrderTitle() : tableCode ? (
                <span>
                  {i18n('KDS.Table')} <big>#{tableCode}</big>
                </span>
              ) : (
                formatOrderType(order.serviceType || SERVICE_TYPE.EAT_HERE, i18n)
              )}
            </OrderType>
            {orderCourseType && (
              <HeaderCourseBadge color={orderCourseType.color}>
                <span>{i18n(orderCourseType.name)}</span>
              </HeaderCourseBadge>
            )}
          </HeaderText>
          {renderHeaderInfoTime()}
        </HeaderInfo>
      </Header>

      {!storage.KDSHideOrderHeader && realOrder?.estimatedFinishTime && storage.showOrderEtaBar && !isFutureOrder && (
        <OrderETA
          orderId={realOrder.id}
          estimatedFinishTime={
            pickupTimeRaw ||
            realOrder?.estimatedFinishTime ||
            DateTime.now()
              .plus({ minutes: incomingAppOrders.prepTime.time })
              .toISO()
          }
          onChangePickupTime={setPickupTimeRaw}
        />
      )}

      {!storage.KDSHideOrderHeader && pickupTime && (
        <DesiredTime styleMultiplier={storage.styleMultiplier}>
          <span
            style={{
              marginRight: 5,
            }}>
            {i18n('KDS.PickupTime')}:
          </span>
          {(pickupTimeRaw && DateTime.fromJSDate(new Date(pickupTimeRaw)).toFormat('HH:mm')) ||
            pickupTime}
        </DesiredTime>
      )}

      {groupByCourseTypes
        ? groupedByCourseTypesItems.map((g, i) => {
            return (
              <OrderGroupItems key={`${order.id}-${g.courseId}`}>
                <CourseTypeName color={g.color}>
                  {g.name && `${i18n(g.name)}`} {g.label && `(${i18n(g.label)})`}
                </CourseTypeName>
                {g.items.map((item) => {
                  // fade item if we are showing all statuses and the item is not the same as the order status
                  const faded =
                    station &&
                    storage.KDSShowAllOrderStatuses &&
                    !station?.orderStatuses.includes(item.status);

                  return (
                    <OrderItemWrapper
                      key={item.id}
                      padding={false}
                      isGroup={false}
                      itemStatusColor={statusBackgroundColor(item.status)}
                      faded={faded}>
                      <OrderItem
                        isBuffering={isBuffering}
                        item={item}
                        count={item.count}
                        isChild={false}
                        progressItem={progressItem}
                        disableItemProgression={
                          (!!realOrder && !storage.enableNewKDSAppOrders) ||
                          storage.disableItemProgress
                        }
                      />
                    </OrderItemWrapper>
                  );
                })}
              </OrderGroupItems>
            );
          })
        : Object.values(stackedItems).map((item, i) => {
            const nextItem = stackedItems[i + 1];
            const padding = !!nextItem && item.parentId !== nextItem.parentId;
            const isChild = !!item.parentId && item.parentId !== item.id;
            // fade item if we are showing all statuses and the item is not the same as the order status
            const faded =
              station &&
              storage.KDSShowAllOrderStatuses &&
              !station?.orderStatuses.includes(item.status);

            return (
              <OrderItemWrapper
                key={item.id}
                padding={padding}
                isGroup={!!item.parentId}
                itemStatusColor={statusBackgroundColor(item.status)}
                faded={faded}>
                <OrderItem
                  isBuffering={isBuffering}
                  item={item}
                  count={item.count}
                  isChild={isChild}
                  progressItem={progressItem}
                  disableItemProgression={
                    (!!realOrder && !storage.enableNewKDSAppOrders) || storage.disableItemProgress
                  }
                />
              </OrderItemWrapper>
            );
          })}

      {order.comment && (
        <Comment display="flex" flexDirection="column">
          <span>{extractCleanComment(order.comment)}</span>
        </Comment>
      )}

      {/* Always show the Origin component if THIRD_PARTY */}
      {((storage.showOrderBottomBar && !tableCode) ||
        order.origin === ORDER_ORIGIN.THIRD_PARTY) && (
        <Origin thirdPartyMetadata={thirdPartyMetadata} realOrder={realOrder} order={order} />
      )}
    </Wrapper>
  );
};

export default React.memo(OrderComponent, (prev, current) => {
  if (prev.courseTypes !== current.courseTypes) {
    return false;
  }

  if (prev.itemsWithCourseType !== current.itemsWithCourseType) {
    return false;
  }

  if (prev.order.status !== current.order.status) {
    return false;
  }

  if (prev.items.length !== current.items.length) {
    return false;
  }

  for (let i = 0; i < prev.items.length; i++) {
    if (prev.items[i].status !== current.items[i].status) {
      return false;
    }
  }

  return true;
});
